import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { usePublicReleaseNotes, IReleaseNote } from '../../services';
import { RouteConstants } from '../../Routes';
import { Dialog, DialogTitle, DialogBody, DialogActions, Button, Badge, Text } from '../UI';

const LAST_SEEN_VERSION_KEY = 'last-seen-release-version';

type BadgeColor = 'zinc' | 'red' | 'emerald' | 'blue';

const categoryBadgeColor = (category: string): BadgeColor => {
    switch (category) {
        case 'feature': return 'emerald';
        case 'bug_fix': return 'red';
        case 'improvement': return 'blue';
        default: return 'zinc';
    }
};

const categoryLabel = (category: string): string => {
    switch (category) {
        case 'feature': return 'Feature';
        case 'bug_fix': return 'Bug Fix';
        case 'improvement': return 'Improvement';
        default: return category;
    }
};

// #region WhatsNewModal
export const WhatsNewModal: React.FunctionComponent = (): React.ReactElement | null => {
    const navigate = useNavigate();
    const [, , { listPublic, getLatestVersion }] = usePublicReleaseNotes();
    const [open, setOpen] = React.useState(false);
    const [newNotes, setNewNotes] = React.useState<IReleaseNote[]>([]);
    const [latestVersion, setLatestVersion] = React.useState<string | null>(null);
    const [checked, setChecked] = React.useState(false);

    React.useEffect(() => {
        if (checked) return;
        checkForNewReleases();
    }, [checked]);

    // #region checkForNewReleases
    const checkForNewReleases = async () => {
        try {
            const version = await getLatestVersion();
            if (!version) { setChecked(true); return; }

            const lastSeen = localStorage.getItem(LAST_SEEN_VERSION_KEY);
            if (lastSeen === version) { setChecked(true); return; }

            setLatestVersion(version);
            const notes = await listPublic(50, 0);
            const unseenNotes = lastSeen
                ? notes.filter((n: IReleaseNote) => n.version > lastSeen)
                : notes;

            if (unseenNotes.length > 0) {
                setNewNotes(unseenNotes);
                setOpen(true);
            }
        } catch {
            // Silently fail — modal is non-critical
        }
        setChecked(true);
    };

    // #region dismiss
    const dismiss = () => {
        if (latestVersion) {
            try { localStorage.setItem(LAST_SEEN_VERSION_KEY, latestVersion); } catch { /* ignore */ }
        }
        setOpen(false);
    };

    // #region seeWhatsNew
    const seeWhatsNew = () => {
        setOpen(false);
        navigate(RouteConstants.RELEASE_NOTES + '?tab=whats-new');
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={dismiss} size="xl">
            <DialogTitle>
                <div className="flex items-center gap-x-2">
                    <SparklesIcon className="h-6 w-6 text-emerald-500" />
                    <span>What&apos;s changed since you&apos;ve been gone</span>
                </div>
            </DialogTitle>
            <DialogBody>
                <div className="flex flex-col gap-y-3 max-h-[400px] overflow-y-auto">
                    {newNotes.map((note: IReleaseNote) => (
                        <div
                            key={note.id}
                            className="flex items-start justify-between gap-x-3 p-3 rounded-lg dark:bg-stone-800/50 bg-surface-muted border dark:border-stone-700 border-stone-200"
                        >
                            <div className="flex flex-col gap-y-1 flex-1">
                                <div className="flex items-center gap-x-2">
                                    <Text className="font-semibold text-sm">{note.title}</Text>
                                    <Badge color={categoryBadgeColor(note.category)}>
                                        {categoryLabel(note.category)}
                                    </Badge>
                                </div>
                                <Text className="text-xs dark:text-gray-400 text-gray-500">
                                    v{note.version} &middot; {note.created_at ? new Date(note.created_at).toLocaleDateString() : ''}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            </DialogBody>
            <DialogActions>
                <Button color="zinc" onClick={dismiss}>Dismiss</Button>
                <Button color="emerald" onClick={seeWhatsNew}>
                    <SparklesIcon data-slot="icon" />
                    See what&apos;s new
                </Button>
            </DialogActions>
        </Dialog>
    );
};
